// Approval chains (D8) — the per-deal Buyer Portal Endorsement Records: one
// append-only row per Licensed Intermediary endorsement (document, version,
// the body's SHA-256, who endorsed and when, which portal received it), with a
// CSV export of the same rows from the server.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Download } from "lucide-react";
import {
  Card,
  SubTable,
  Tooltip,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { ApiError, extractErrorMessage } from "@/api/client";
import {
  approvalChainsApi,
  endorsementRecordsExportUrl,
  type EndorsementRecordDto,
} from "@/api/approvalChains";
import { approvalChainKeys } from "@/hooks/collection/approvalChainKeys";
import { useDateFormatter } from "@/utils/datetime";

const SHA_PREFIX_LENGTH = 12;

function ShaCell({ sha }: { sha: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(sha);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-space-1">
      <Tooltip label={sha}>
        <code tabIndex={0} className="font-mono text-caption text-onyx" data-testid="endorsement-sha">
          {sha.slice(0, SHA_PREFIX_LENGTH)}…
        </code>
      </Tooltip>
      <button
        type="button"
        onClick={() => void copy()}
        aria-label={copied ? "SHA-256 copied" : "Copy the full SHA-256"}
        className="inline-flex items-center text-gray-mid hover:text-onyx"
      >
        {copied ? <Check size={12} aria-hidden /> : <Copy size={12} aria-hidden />}
      </button>
    </span>
  );
}

export function EndorsementRecordsSection({
  ownerEntityType = "Deal",
  ownerEntityId,
}: {
  ownerEntityType?: string;
  ownerEntityId: string;
}) {
  const { dateTime } = useDateFormatter();
  const query = useQuery({
    queryKey: approvalChainKeys.endorsementRecords(ownerEntityType, ownerEntityId),
    queryFn: () => approvalChainsApi.endorsementRecords(ownerEntityType, ownerEntityId),
  });
  const rows = query.data ?? [];
  // The records need Collection.ReadPackages; a viewer without it sees the
  // rest of the tab, not an error card.
  if (query.error instanceof ApiError && query.error.status === 403) return null;

  const columns: SubTableColumn<EndorsementRecordDto>[] = [
    { key: "document", header: "Document", render: (r) => r.documentName, sortAccessor: (r) => r.documentName },
    { key: "version", header: "Version", render: (r) => r.documentVersion, sortAccessor: (r) => r.documentVersion },
    { key: "sha", header: "Body SHA-256", render: (r) => <ShaCell sha={r.bodySha256} />, searchText: (r) => r.bodySha256 },
    {
      key: "intermediary",
      header: "Licensed Intermediary",
      render: (r) => (
        <span className="flex flex-col">
          <span>{r.intermediaryName}</span>
          <span className="text-caption text-gray-mid">{r.intermediaryBrokerageName ?? "—"}</span>
        </span>
      ),
      searchText: (r) => `${r.intermediaryName} ${r.intermediaryBrokerageName ?? ""}`,
      sortAccessor: (r) => r.intermediaryName,
    },
    {
      key: "endorsedAt",
      header: "Endorsed at",
      render: (r) => dateTime(r.endorsedAt),
      sortAccessor: (r) => r.endorsedAt,
    },
    { key: "portal", header: "Receiving portal", render: (r) => r.receivingPortal, sortAccessor: (r) => r.receivingPortal },
  ];

  return (
    <Card data-testid="endorsement-records-section">
      <div className="flex items-start justify-between flex-wrap gap-space-2 mb-space-3">
        <div>
          <div className="font-display font-bold text-h3 text-onyx">Endorsement Records</div>
          <p className="text-small text-gray-mid m-0">
            Every Licensed Intermediary endorsement of a document version on this deal, with the
            fingerprint (SHA-256) of the exact body endorsed. Records are permanent.
          </p>
        </div>
        <a
          href={endorsementRecordsExportUrl(ownerEntityType, ownerEntityId)}
          download
          className="inline-flex items-center gap-space-1 rounded-sm border border-gold px-space-3 py-space-1 text-label tracking-button text-onyx hover:bg-gold-highlight/10"
          data-testid="endorsement-records-export"
        >
          <Download size={14} aria-hidden /> Export CSV
        </a>
      </div>
      {query.isLoading ? (
        <p className="text-caption text-gray-mid m-0">Loading endorsement records…</p>
      ) : query.isError ? (
        <p className="text-small text-status-error m-0" role="alert">
          Could not load the endorsement records: {extractErrorMessage(query.error)}
        </p>
      ) : (
        <SubTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          caption="Endorsement records"
          empty={{
            message: "No endorsements on this deal yet.",
            door: {
              kind: "derived",
              explain: "A record is written each time the Licensed Intermediary endorses a document.",
            },
          }}
        />
      )}
    </Card>
  );
}
